/**
 * Aba Planilhas (C5) — fiel ao mockup (.file-source). Reusa o DadosContext
 * (mesma lógica da antiga página /configuracoes): planilha principal
 * (Gerenciador de Produção) + Distribuição de Projetos. No topo, a planilha
 * principal lida do Google Drive pelo servidor (PlanilhaGoogleDrive); o
 * seletor de arquivo abaixo continua como plano B.
 */
import React, { useRef, useState } from "react";

import { MODULO_PLANILHA_LEGADO, MODULO_PROJETOS_EXECUTIVOS } from "../../../config/modulos";
import { useAuth } from "../../../context/AuthContext";
import { useDados } from "../../../context/DadosContext";
import { useToast } from "../../../context/ToastContext";
import OrdemDoExecutivo from "./OrdemDoExecutivo";
import PlanilhaGoogleDrive from "./PlanilhaGoogleDrive";

const IcFile = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);
const IcSelect = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
  </svg>
);
const IcRefresh = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
    <path d="M3 12a9 9 0 1 0 9-9" /><path d="M3 4v5h5" />
  </svg>
);
const IcTrash = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

function FileSource({ ativo, vazio, nome, meta, children }) {
  return (
    <div className="file-source">
      <div className="file-source__active">{ativo}</div>
      <div className={`file-source__name ${vazio ? "empty" : ""}`}>
        <span className="ic">{IcFile}</span> {nome}
      </div>
      <div className="file-source__meta">{meta}</div>
      <div className="file-source__actions">{children}</div>
    </div>
  );
}

export default function PlanilhasTab() {
  const toast = useToast();
  // Cada planilha é de um módulo vendido à parte (ADR-0102): só aparece a do
  // módulo que a unidade contratou.
  const { temModulo } = useAuth();
  const comGerenciador = Boolean(temModulo?.(MODULO_PLANILHA_LEGADO));
  const comDistribuicao = Boolean(temModulo?.(MODULO_PROJETOS_EXECUTIVOS));
  const fileRef = useRef(null);
  const fileDistRef = useRef(null);
  const {
    loading,
    meta,
    statusPlanilha,
    planilhaErro,
    alterandoPlanilha,
    selecionarPlanilha,
    selecionarPlanilhaPersistente,
    suportaSelecaoPersistente,
    uploadPlanilha,
    recarregarDados,
    removerPlanilha,
    metaDistribuicao,
    statusPlanilhaDist,
    planilhaDistErro,
    alterandoPlanilhaDist,
    uploadPlanilhaDist,
    recarregarPlanilhaDist,
    removerPlanilhaDist,
  } = useDados();
  const [busy, setBusy] = useState(false);

  const arquivoAtual =
    statusPlanilha?.arquivoEncontrado ||
    meta?.arquivoLido ||
    (loading ? "Carregando planilha…" : "Nenhuma planilha selecionada");
  const semPrincipal = !statusPlanilha?.arquivoEncontrado && !meta?.arquivoLido;
  const detalhePrincipal =
    planilhaErro ||
    statusPlanilha?.caminhoAtual ||
    meta?.caminhoArquivo ||
    'Use "Selecionar planilha" para apontar a planilha .xlsm/.xlsx';

  const arquivoDist =
    statusPlanilhaDist?.arquivoEncontrado ||
    metaDistribuicao?.arquivoLido ||
    "Nenhuma planilha selecionada";
  const semDist = !statusPlanilhaDist?.arquivoEncontrado && !metaDistribuicao?.arquivoLido;
  const detalheDist =
    planilhaDistErro ||
    (metaDistribuicao?.totalAmbientes != null
      ? `${metaDistribuicao.totalAmbientes} ambientes ativos lidos`
      : "Selecione a planilha de distribuição de projetos (.xlsm)");

  async function handleSelecionarPrincipal() {
    if (suportaSelecaoPersistente) {
      try {
        const r = await selecionarPlanilhaPersistente();
        if (r?.cancelado) return;
        toast.success(r?.acessoPersistente ? "Planilha selecionada (atualização automática)." : "Planilha carregada.");
      } catch (err) {
        toast.error(err?.message || "Falha ao selecionar.");
      }
      return;
    }
    try {
      const r = await selecionarPlanilha();
      if (r?.origem === "navegador") { fileRef.current?.click(); return; }
      if (r?.cancelado) return;
      toast.success("Planilha selecionada.");
    } catch (err) {
      toast.error(err?.message || "Falha ao selecionar.");
    }
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { await uploadPlanilha(file); toast.success("Planilha carregada."); }
    catch (err) { toast.error(err?.message || "Falha no upload."); }
  }

  async function wrap(fn, ok) {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); }
    catch (err) { toast.error(err?.message || "Erro."); }
    finally { setBusy(false); }
  }

  async function onFileDist(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { await uploadPlanilhaDist(file); toast.success("Distribuição carregada."); }
    catch (err) { toast.error(err?.message || "Falha no upload."); }
  }

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Planilhas</h2>
        <p className="set-section__desc">Fontes de dados conectadas ao PharuERP.</p>
      </div>
      <div className="set-section__body">
        {comGerenciador && (
          <>
            <PlanilhaGoogleDrive />
            <OrdemDoExecutivo />

            <div className="set-row">
              <div>
                <div className="set-row__lbl">Planilha principal</div>
                <div className="set-row__desc">Gerenciador de produção — base de pedidos, OPs e clientes.</div>
              </div>
              <div className="set-row__control">
                <FileSource ativo="Arquivo ativo" vazio={semPrincipal} nome={arquivoAtual} meta={detalhePrincipal}>
                  <button className="btn btn--secondary btn--sm" type="button" disabled={alterandoPlanilha} onClick={handleSelecionarPrincipal}>
                    {IcSelect} {alterandoPlanilha ? "Abrindo…" : "Selecionar planilha"}
                  </button>
                  <button className="btn btn--ghost btn--sm" type="button" disabled={loading || busy} onClick={() => wrap(recarregarDados, "Dados atualizados.")}>
                    {IcRefresh} Atualizar dados
                  </button>
                  <button className="btn btn--ghost btn--sm" type="button" style={{ color: "var(--danger-text)" }} onClick={() => { removerPlanilha(); toast.success("Planilha removida deste navegador."); }}>
                    {IcTrash} Remover salva
                  </button>
                </FileSource>
                <input ref={fileRef} type="file" accept=".xlsm,.xlsx" hidden onChange={onFile} />
              </div>
            </div>
          </>
        )}

        {comDistribuicao && (
          <div className="set-row">
            <div>
              <div className="set-row__lbl">Distribuição de projetos</div>
              <div className="set-row__desc">Planilha de alocação dos projetos por equipe.</div>
            </div>
            <div className="set-row__control">
              <FileSource ativo="Arquivo ativo" vazio={semDist} nome={arquivoDist} meta={detalheDist}>
                <button className="btn btn--secondary btn--sm" type="button" disabled={alterandoPlanilhaDist} onClick={() => fileDistRef.current?.click()}>
                  {IcSelect} {alterandoPlanilhaDist ? "Abrindo…" : "Selecionar planilha"}
                </button>
                <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={() => wrap(recarregarPlanilhaDist, "Distribuição atualizada.")}>
                  {IcRefresh} Atualizar dados
                </button>
                <button className="btn btn--ghost btn--sm" type="button" style={{ color: "var(--danger-text)" }} onClick={() => { removerPlanilhaDist(); toast.success("Distribuição removida deste navegador."); }}>
                  {IcTrash} Remover salva
                </button>
              </FileSource>
              <input ref={fileDistRef} type="file" accept=".xlsm,.xlsx" hidden onChange={onFileDist} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
